import { IsEqual } from "type-fest";

import { HqlAggregationFunction, HqlScalarFunction } from "../hql/types.js";

export const CalcParameterType = {
  BOOLEAN: "boolean",
  NUMBER: "number",
  TEXT: "text",
  DATETIME: "datetime",
  GENERIC_T: "T",
  GENERIC_U: "U",
} as const;
export type CalcParameterType =
  (typeof CalcParameterType)[keyof typeof CalcParameterType];

export const CALC_PARAMETER_TYPE_DISPLAY_NAMES: Record<
  CalcParameterType,
  string
> = {
  [CalcParameterType.BOOLEAN]: "boolean",
  [CalcParameterType.NUMBER]: "number",
  [CalcParameterType.TEXT]: "text",
  [CalcParameterType.DATETIME]: "datetime",
  [CalcParameterType.GENERIC_T]: "value",
  [CalcParameterType.GENERIC_U]: "value",
};

export const CalcParameterKind = {
  LITERAL_SCALAR: "literal_scalar",
  SCALAR: "scalar",
  LITERAL_COLUMN: "literal_column",
  COLUMN: "column",
  ANY: "any",
} as const;
export type CalcParameterKind =
  (typeof CalcParameterKind)[keyof typeof CalcParameterKind];

export interface CalcParameterInfo {
  name: string;
  type: CalcParameterType;
  kind: CalcParameterKind;
  documentation?: string;
  variadic?: boolean;
}

export interface CalcFunctionInfo {
  name: string;
  returnType: CalcParameterType;
  documentation: string;
  /** tuple of expression examples and results */
  examples?: readonly string[];
  parameters: readonly CalcParameterInfo[];
}

type NamesUsed = (typeof RAW_FUNC_DICT)[number]["name"];

type IsTrue<T extends true> = T;
type _AssertAllNamesCovered = IsTrue<
  IsEqual<
    NamesUsed,
    | Exclude<HqlScalarFunction, "DatetimeToEpochMs" | "EpochMsToDatetime">
    | HqlAggregationFunction
  >
>;

// convert array into object keyed by lowercase function name for quick lookup
function makeFunctionDictionary(
  functions: readonly CalcFunctionInfo[],
): Record<string, CalcFunctionInfo[]> {
  return functions.reduce(
    (acc, f) => {
      const key = f.name.toLowerCase();
      return {
        ...acc,
        [key]: [...(acc[key] ?? []), f],
      };
    },
    {} as Record<string, CalcFunctionInfo[]>,
  );
}

const RAW_FUNC_DICT = [
  // NUMERIC functions
  {
    name: HqlScalarFunction.Abs,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the absolute value of `number`.",
    examples: ["Abs(-1) → 1", "Abs(5.3) → 5.3"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Round,
    returnType: CalcParameterType.NUMBER,
    documentation: "Rounds `number` up or down to the nearest integer.",
    examples: ["Round(3.14) → 3"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Ceiling,
    returnType: CalcParameterType.NUMBER,
    documentation: "Rounds up `number` to the nearest integer.",
    examples: ["Ceiling(3.14) → 4"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Floor,
    returnType: CalcParameterType.NUMBER,
    documentation: "Rounds down `number` to the nearest integer.",
    examples: ["Floor(3.14) → 3"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Power,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Raises `number` to the power of `power`. You can also use the `^` operator instead.",
    examples: ["Power(3, 2) → 9"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "power",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Sqrt,
    returnType: CalcParameterType.NUMBER,
    examples: ["Sqrt(9) → 3"],
    documentation: "Takes the square root of `number`.",
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Exp,
    returnType: CalcParameterType.NUMBER,
    examples: ["Exp(1) →2.718281828459045"],
    documentation:
      "Raises the mathematical constant e to the power of `power`.",
    parameters: [
      {
        name: "power",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },

  // TEXT functions
  {
    name: HqlScalarFunction.Lower,
    returnType: CalcParameterType.TEXT,
    examples: ["Lower('Jane Doe') → 'jane doe'"],
    documentation: "Converts text to lowercase.",
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Upper,
    returnType: CalcParameterType.TEXT,
    documentation: "Converts text to uppercase.",
    examples: ["Upper('Jane Doe') → 'JANE DOE'"],

    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Concat,
    returnType: CalcParameterType.TEXT,
    documentation:
      "Concatenates multiple `text` values into a single text value. Equivalent to the `&` operator.",
    examples: ["Concat('Jane', ' ', 'Doe') → 'Jane Doe'"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
        variadic: true,
      },
    ],
  },
  {
    name: HqlScalarFunction.Contains,
    returnType: CalcParameterType.BOOLEAN,
    documentation:
      "Returns `True` if `value` contains `search_text`, and `False` otherwise.",
    examples: ["Contains('abcd', 'abc') → True"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "search_text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.LITERAL_SCALAR,
      },
    ],
  },
  {
    name: HqlScalarFunction.StartsWith,
    returnType: CalcParameterType.BOOLEAN,
    documentation:
      "Returns `True` if `text` starts with `search_text`, and `False` otherwise.",
    examples: ["StartsWith('abcd', 'abc') → True"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "search_text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.LITERAL_SCALAR,
      },
    ],
  },
  {
    name: HqlScalarFunction.EndsWith,
    returnType: CalcParameterType.BOOLEAN,
    documentation:
      "Returns `True` if `text` ends with `search_text`, and `False` otherwise.",
    examples: ["EndsWith('abcd', 'abc') → True"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "search_text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.LITERAL_SCALAR,
      },
    ],
  },
  {
    name: HqlScalarFunction.Length,
    returnType: CalcParameterType.NUMBER,
    documentation: "Counts the number of characters in `text`.",
    examples: ["Length('Hex') → 3"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Left,
    returnType: CalcParameterType.TEXT,
    documentation: "Returns the first `n` characters of `text`.",
    examples: ["Left('abcd', 3) → 'abc'"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "n",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Right,
    returnType: CalcParameterType.TEXT,
    documentation: "Returns the last `n` characters of `text`.",
    examples: ["Right('abcd', 3) → 'bcd'"],
    parameters: [
      {
        name: "value",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "n",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Substitute,
    returnType: CalcParameterType.TEXT,
    documentation:
      "Changes all occurrences of `search_text` in `value` to be `replacement_text`.",
    examples: ["Substitute('abcabc', 'bc', 'BC') → 'aBCaBC'"],
    parameters: [
      {
        name: "value",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "search_text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "replacement_text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.SplitPart,
    returnType: CalcParameterType.TEXT,
    documentation:
      "Splits `text` into separate parts separated by `delimiter` and returns the `part_number` part.",
    examples: ["SplitPart('jane@example.com', '@', 1) → 'jane'"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "delimiter",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "part_number",
        type: CalcParameterType.NUMBER,
        // most backends support this as a dynamic parameter (as of Q2 2025),
        // but MSSQL doesn't and it's pretty niche, so enforce a constant here
        kind: CalcParameterKind.LITERAL_SCALAR,
      },
    ],
  },

  // NULL handling functions
  {
    name: HqlScalarFunction.IsNull,
    returnType: CalcParameterType.BOOLEAN,
    documentation: "Returns `True` if `value` is null, `False` otherwise.",
    examples: ["IsNull(null) → True", "IsNull(5) → False"],
    parameters: [
      {
        name: "value",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.IsFinite,
    returnType: CalcParameterType.BOOLEAN,
    documentation:
      "Returns `True` if `number` is not null, NaN, Infinity, or not Infinity, `False` otherwise.",
    examples: ["IsFinite(5) → True", "IsFinite(1 / 0) → False"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Coalesce,
    returnType: CalcParameterType.GENERIC_T,
    documentation: "Returns the first non-null `value`.",
    examples: ["Coalesce(null, 0, 42) → 0"],
    parameters: [
      {
        name: "value",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
        variadic: true,
      },
    ],
  },
  // Cast functions
  {
    name: HqlScalarFunction.ToText,
    returnType: CalcParameterType.TEXT,
    documentation: "Converts `number` to text.",
    examples: ["ToText(5.7) → '5.7'", "ToText(2.0) → '2'"],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.ToNumber,
    returnType: CalcParameterType.NUMBER,
    documentation: "Converts `text` to a number.",
    examples: ["ToNumber('5.7') → 5.7"],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.ToDatetime,
    returnType: CalcParameterType.DATETIME,
    documentation: "Converts `text` to a datetime.",
    examples: [
      "ToDatetime('2024-01-01') → 2021-01-01 00:00:00",
      "ToDatetime('2024-01-02 03:04:05') → 2024-01-02 03:04:05",
    ],
    parameters: [
      {
        name: "text",
        type: CalcParameterType.TEXT,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.ToBoolean,
    returnType: CalcParameterType.BOOLEAN,
    documentation:
      "Converts `number` into a boolean data type, where 0 is `False` and 1 is `True`.",
    examples: [
      "ToBoolean(0) → False",
      "ToBoolean(1) → True",
      "ToBoolean(5) → True",
    ],
    parameters: [
      {
        name: "number",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  // DATETIME functions
  {
    name: HqlScalarFunction.Year,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the year from `datetime`.",
    examples: ["Year(ToDatetime('2024-01-02')) -> 2024"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Quarter,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the quarter from `datetime`.",
    examples: ["Quarter(ToDatetime('2024-04-05')) -> 2"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Month,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the month from `datetime` (1 to 12).",
    examples: ["Month(ToDatetime('2024-01-02')) -> 1"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Day,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the day of the month from `datetime` (1 to 31).",
    examples: ["Day(ToDatetime('2024-01-02')) -> 2"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DayOfWeek,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Extracts the day of the week from `datetime` (1 for Sunday, 7 for Saturday).",
    examples: ["DayOfWeek(ToDatetime('2024-01-02')) -> 3"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Hour,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the hour from `datetime` (0 to 23).",
    examples: ["Hour(ToDatetime('2024-01-02 03:04:05')) -> 3"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Minute,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the minute from `datetime` (0 to 59).",
    examples: ["Minute(ToDatetime('2024-01-02 03:04:05')) -> 4"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Second,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the second from `datetime` (0 to 59).",
    examples: ["Second(ToDatetime('2024-01-02 03:04:05')) -> 5"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Millisecond,
    returnType: CalcParameterType.NUMBER,
    documentation: "Extracts the millisecond from `datetime` (0 to 999).",
    // DuckDB doesn't support milliseconds in datetime strings right now,
    // so just leaving out this example temporarily.
    // See https://hex-tech-hq.slack.com/archives/C06570ALZ7F/p1716243047360339
    // examples: ["Millisecond(ToDatetime('2024-01-02 03:04:05.678')) -> 678"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncYear,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest year.",
    examples: ["TruncYear(ToDatetime('2024-02-03')) -> 2024-01-01"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncQuarter,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest quarter.",
    examples: ["TruncQuarter(ToDatetime('2024-05-06')) -> 2024-04-01"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncMonth,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest month.",
    examples: ["TruncMonth(ToDatetime('2024-02-03')) -> 2024-02-01"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncWeek,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest Sunday.",
    examples: ["TruncWeek(ToDatetime('2024-02-03')) -> 2024-01-28"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncWeekMonday,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest Monday.",
    examples: ["TruncWeek(ToDatetime('2024-02-03')) -> 2024-01-29"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncDay,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest day.",
    examples: ["TruncDay(ToDatetime('2024-02-03 04:05:06')) -> 2024-02-03"],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncHour,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest hour.",
    examples: [
      "TruncHour(ToDatetime('2024-02-03 04:05:06')) -> 2024-02-03 04:00:00",
    ],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncMinute,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest minute.",
    examples: [
      "TruncMinute(ToDatetime('2024-02-03 04:05:06')) -> 2024-02-03 04:05:00",
    ],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.TruncSecond,
    returnType: CalcParameterType.DATETIME,
    documentation: "Truncates `datetime` down to the nearest second.",
    examples: [
      "TruncSecond(ToDatetime('2024-02-03 04:05:06.789')) -> 2024-02-03 04:05:06",
    ],
    parameters: [
      {
        name: "datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DiffDays,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Calculates the number of days between `start_datetime` and `end_datetime`." +
      " If `start_datetime` is after `end_datetime`, the result will be negative.",
    examples: [
      "DiffDays(ToDatetime('2024-02-03 04:00:00'), ToDatetime('2024-02-04 16:00:00')) -> 1.5",
    ],
    parameters: [
      {
        name: "start_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "end_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DiffHours,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Calculates the number of hours between `start_datetime` and `end_datetime`." +
      " If `start_datetime` is after `end_datetime`, the result will be negative.",
    examples: [
      "DiffHours(ToDatetime('2024-02-03 04:00:00'), ToDatetime('2024-02-03 05:30:00')) -> 1.5",
    ],
    parameters: [
      {
        name: "start_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "end_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DiffMinutes,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Calculates the number of minutes between `start_datetime` and `end_datetime`." +
      " If `start_datetime` is after `end_datetime`, the result will be negative.",
    examples: [
      "DiffMinutes(ToDatetime('2024-02-03 04:15:00'), ToDatetime('2024-02-03 04:16:30')) -> 1.5",
    ],
    parameters: [
      {
        name: "start_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "end_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DiffSeconds,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Calculates the number of seconds between `start_datetime` and `end_datetime`." +
      " If `start_datetime` is after `end_datetime`, the result will be negative.",
    examples: [
      "DiffSeconds(ToDatetime('2024-02-03 04:05:06'), ToDatetime('2024-02-03 04:05:08')) -> 2",
    ],
    parameters: [
      {
        name: "start_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "end_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.DiffMilliseconds,
    returnType: CalcParameterType.NUMBER,
    documentation:
      "Calculates the number of milliseconds between `start_datetime` and `end_datetime`." +
      " If `start_datetime` is after `end_datetime`, the result will be negative.",
    examples: [
      "DiffMilliseconds(ToDatetime('2024-02-03 04:05:06.101'), ToDatetime('2024-02-03 04:05:06.202')) -> 101",
    ],
    parameters: [
      {
        name: "start_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "end_datetime",
        type: CalcParameterType.DATETIME,
        kind: CalcParameterKind.ANY,
      },
    ],
  },

  // Now() / Today()
  {
    name: HqlScalarFunction.Now,
    returnType: CalcParameterType.DATETIME,
    documentation: "Returns the current date and time.",
    examples: ["Now() -> 2024-02-03 04:05:06.789"],
    parameters: [],
  },
  {
    name: HqlScalarFunction.Today,
    returnType: CalcParameterType.DATETIME,
    documentation: "Returns the current date.",
    examples: ["Today() -> 2024-02-03"],
    parameters: [],
  },

  // Control functions
  {
    name: HqlScalarFunction.If,
    returnType: CalcParameterType.GENERIC_T,
    documentation:
      "Returns `value_if_true` if the first argument is `True`, otherwise return `value_if_false`.",
    expamples: ["If(1 > 2, 'Yes', 'No') → 'No'"],
    parameters: [
      {
        name: "condition",
        type: CalcParameterType.BOOLEAN,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "value_if_true",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "value_if_false",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlScalarFunction.Switch,
    returnType: CalcParameterType.GENERIC_U,
    documentation:
      "A more succinct way of nesting many `If()` statements based on a single input value. Returns `result` corresponding to the paired `if_matches` value that equals the `switch_value`.",
    examples: [
      "Switch(status_code, 1, 'Processing', 2, 'Shipped', 3, 'Delivered')",
    ],
    parameters: [
      {
        name: "switch_value",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "if_matches",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
        variadic: true,
      },
      {
        name: "result",
        type: CalcParameterType.GENERIC_U,
        kind: CalcParameterKind.ANY,
        variadic: true,
      },
    ],
  },
  {
    name: HqlScalarFunction.IsOneOf,
    returnType: CalcParameterType.BOOLEAN,
    documentation: "Returns `True` if `value` equals one of `match`.",
    parameters: [
      {
        name: "value",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
      {
        name: "match",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.LITERAL_SCALAR,
        variadic: true,
      },
    ],
  },
  // Aggregate functions
  {
    name: HqlAggregationFunction.Avg,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the mean of `column` ignoring null values.",
    examples: ["Avg(column) → 2.53"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Count,
    returnType: CalcParameterType.NUMBER,
    documentation: "Counts the number of rows.",
    examples: ["Count() → 1640"],
    parameters: [],
  },
  {
    name: HqlAggregationFunction.Count,
    returnType: CalcParameterType.NUMBER,
    documentation: "Counts the number of all values in `column`.",
    examples: ["Count(column) → 1640"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.CountDistinct,
    returnType: CalcParameterType.NUMBER,
    documentation: "Counts the number of unique values in `column`.",
    examples: ["CountDistinct(column) → 854"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Max,
    returnType: CalcParameterType.GENERIC_T,
    documentation: "Returns the largest value in `column`.",
    examples: ["Max(column) → 5007"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Median,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the median of `column`.",
    examples: ["Median(column) → 52"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Min,
    returnType: CalcParameterType.GENERIC_T,
    documentation: "Returns the smallest value in `column`.",
    examples: ["Min(column) → -47"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.GENERIC_T,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.StdDev,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the sample standard deviation of `column`.",
    examples: ["StdDev(column) → 1.43"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.StdDevPop,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the population standard deviation of `column`.",
    examples: ["StdDevPop(column) → 1.47"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Sum,
    returnType: CalcParameterType.NUMBER,
    documentation: "Returns the sum of all values in `column`.",
    examples: ["Sum(column) → 387"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.Variance,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the sample variance of `column`.",
    examples: ["Variance(column) → 2.12"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
  {
    name: HqlAggregationFunction.VariancePop,
    returnType: CalcParameterType.NUMBER,
    documentation: "Computes the population variance of `column`.",
    examples: ["VariancePop(column) → 2.42"],
    parameters: [
      {
        name: "column",
        type: CalcParameterType.NUMBER,
        kind: CalcParameterKind.ANY,
      },
    ],
  },
] as const;

export const CALC_FUNCTION_DICTIONARY = makeFunctionDictionary(RAW_FUNC_DICT);

export const getCalcFunctionInfo = (
  functionName: string,
): CalcFunctionInfo[] | undefined =>
  CALC_FUNCTION_DICTIONARY[functionName.toLowerCase()];
